import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { usePresenceStore } from './presenceStore'
import type { BandDto, UpdateBandMemberDto } from '../dtos'

export const useBandStore = defineStore('band', () => {
    const userStore = useUserStore()
    const presenceStore = usePresenceStore()
    const bands = ref<BandDto[]>([])
    const currentBand = ref<BandDto | null>(null)
    const loading = ref(false)
    const initialLoading = ref(false)
    const isFetching = ref(false)

    const myMembership = computed(() => {
        if (!currentBand.value || !userStore.dbUser) return null
        return currentBand.value.members.find(m => m.userId === userStore.dbUser!.id) || null
    })

    const canEditSongs = computed(() => {
        const m = myMembership.value
        if (!m) return false
        if (m.role === 'Owner') return true
        return m.canEditSongs
    })

    const canEditMixes = computed(() => {
        const m = myMembership.value
        if (!m) return false
        if (m.role === 'Owner') return true
        return m.canEditMixes
    })

    const canEditSetlists = computed(() => {
        const m = myMembership.value
        if (!m) return false
        if (m.role === 'Owner') return true
        return m.canEditSetlists
    })

    const canEditEvents = computed(() => {
        const m = myMembership.value
        if (!m) return false
        if (m.role === 'Owner') return true
        return m.canEditEvents
    })

    async function fetchBands() {
        if (!userStore.user) return
        
        loading.value = true
        isFetching.value = true
        // Only set initialLoading if we don't have bands yet
        if (bands.value.length === 0) {
            initialLoading.value = true
        }

        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get<BandDto[]>('/bands', {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Force array to prevent "not iterable" errors
            bands.value = Array.isArray(response.data) ? response.data : []
            
            // Restore selection if saved in user profile
            if (userStore.dbUser?.lastSelectedBandId && bands.value.length > 0) {
                const savedBand = bands.value.find(b => b.id === userStore.dbUser?.lastSelectedBandId)
                if (savedBand) {
                    currentBand.value = savedBand
                }
            }

            if (bands.value.length > 0 && !currentBand.value) {
                currentBand.value = bands.value[0] || null
            }
        } catch (error) {
            console.error('Failed to fetch bands:', error)
            bands.value = []
        } finally {
            loading.value = false
            initialLoading.value = false
            isFetching.value = false
        }
    }

    async function saveLastBand(bandId: number | null) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.put(`/users/last-band/${bandId || 0}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (userStore.dbUser) {
                userStore.dbUser.lastSelectedBandId = bandId || undefined
            }
        } catch (error) {
            console.error('Failed to save last band preference:', error)
        }
    }

    async function createBand(name: string) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post('/bands', 
                { name },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            bands.value.push(response.data)
            setCurrentBand(response.data)
            return response.data
        } catch (error) {
            console.error('Failed to create band:', error)
            throw error
        }
    }

    function setCurrentBand(band: BandDto | null) {
        currentBand.value = band
        if (band) {
            presenceStore.startPresence('Band', band.id)
            saveLastBand(band.id)
        } else {
            presenceStore.stopPresence()
            saveLastBand(null)
        }
    }

    async function deleteBand(bandId: number) {
        try {
            await api.delete(`/bands/${bandId}`)
            bands.value = bands.value.filter(b => b.id !== bandId)
            if (currentBand.value?.id === bandId) {
                setCurrentBand(bands.value[0] || null)
            }
        } catch (error: any) {
            console.error('Failed to delete band:', error)
            throw error
        }
    }

    async function updateMember(bandId: number, userId: number, data: UpdateBandMemberDto) {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        const response = await api.put(`/bands/${bandId}/members/${userId}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        // Update local state
        const band = bands.value.find(b => b.id === bandId)
        if (band) {
            const member = band.members.find(m => m.userId === userId)
            if (member) Object.assign(member, response.data)
        }
        if (currentBand.value?.id === bandId) {
            const member = currentBand.value.members.find(m => m.userId === userId)
            if (member) Object.assign(member, response.data)
        }
        return response.data
    }

    async function removeMember(bandId: number, userId: number) {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        await api.delete(`/bands/${bandId}/members/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        // Update local state
        const band = bands.value.find(b => b.id === bandId)
        if (band) {
            band.members = band.members.filter(m => m.userId !== userId)
        }
        if (currentBand.value?.id === bandId) {
            currentBand.value.members = currentBand.value.members.filter(m => m.userId !== userId)
        }
    }

    async function leaveBand(bandId: number) {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        await api.post(`/bands/${bandId}/leave`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        })
        bands.value = bands.value.filter(b => b.id !== bandId)
        if (currentBand.value?.id === bandId) {
            currentBand.value = bands.value[0] || null
        }
    }

    async function transferOwnership(bandId: number, newOwnerUserId: number) {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        const response = await api.post(`/bands/${bandId}/transfer-ownership`, { newOwnerUserId }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        const updatedBand = response.data as BandDto
        // Update local state
        const idx = bands.value.findIndex(b => b.id === bandId)
        if (idx !== -1) bands.value[idx] = updatedBand
        if (currentBand.value?.id === bandId) currentBand.value = updatedBand
        return updatedBand
    }

    function clear() {
        bands.value = []
        currentBand.value = null
        loading.value = false
        initialLoading.value = false
        isFetching.value = false
    }

    return { bands, currentBand, loading, initialLoading, isFetching, myMembership, canEditSongs, canEditMixes, canEditSetlists, canEditEvents, fetchBands, createBand, deleteBand, updateMember, removeMember, leaveBand, transferOwnership, setCurrentBand, saveLastBand, clear }
})
